import { useCallback, useMemo, useReducer } from "react";
import type { ClinicalEvent } from "../types/clinicalEvent";

export type ReviewAccelerationState = {
  densityStripVisible: boolean;
  bookmarks: ClinicalEvent[];
  clipQueue: ClinicalEvent[];
  selectedEventType: string | "all";
  baselineComparisonEnabled: boolean;
};

type Action =
  | { type: "TOGGLE_DENSITY_STRIP" }
  | { type: "SET_DENSITY_STRIP"; visible: boolean }
  | { type: "ADD_BOOKMARK"; event: ClinicalEvent }
  | { type: "REMOVE_BOOKMARK"; eventId: string }
  | { type: "ENQUEUE_CLIP"; event: ClinicalEvent }
  | { type: "DEQUEUE_CLIP"; eventId: string }
  | { type: "SET_EVENT_TYPE"; eventType: string | "all" }
  | { type: "TOGGLE_BASELINE_COMPARISON" };

export const initialReviewAccelerationState: ReviewAccelerationState = {
  densityStripVisible: true,
  bookmarks: [],
  clipQueue: [],
  selectedEventType: "all",
  baselineComparisonEnabled: false,
};

export function reviewAccelerationReducer(
  state: ReviewAccelerationState,
  action: Action,
): ReviewAccelerationState {
  switch (action.type) {
    case "TOGGLE_DENSITY_STRIP":
      return { ...state, densityStripVisible: !state.densityStripVisible };
    case "SET_DENSITY_STRIP":
      return { ...state, densityStripVisible: action.visible };
    case "ADD_BOOKMARK":
      if (state.bookmarks.some((item) => item.eventId === action.event.eventId)) {
        return state;
      }
      return { ...state, bookmarks: [...state.bookmarks, action.event] };
    case "REMOVE_BOOKMARK":
      return {
        ...state,
        bookmarks: state.bookmarks.filter(
          (item) => item.eventId !== action.eventId,
        ),
      };
    case "ENQUEUE_CLIP":
      if (state.clipQueue.some((item) => item.eventId === action.event.eventId)) {
        return state;
      }
      return { ...state, clipQueue: [...state.clipQueue, action.event] };
    case "DEQUEUE_CLIP":
      return {
        ...state,
        clipQueue: state.clipQueue.filter(
          (item) => item.eventId !== action.eventId,
        ),
      };
    case "SET_EVENT_TYPE":
      return { ...state, selectedEventType: action.eventType };
    case "TOGGLE_BASELINE_COMPARISON":
      return {
        ...state,
        baselineComparisonEnabled: !state.baselineComparisonEnabled,
      };
    default:
      return state;
  }
}

export function useReviewAccelerationState(
  seed: ReviewAccelerationState = initialReviewAccelerationState,
) {
  const [state, dispatch] = useReducer(reviewAccelerationReducer, seed);

  const jumpCandidates = useCallback(
    (events: ClinicalEvent[]) => {
      if (state.selectedEventType === "all") return events;
      return events.filter(
        (event) =>
          event.eventTypeCode === state.selectedEventType ||
          event.kind === state.selectedEventType,
      );
    },
    [state.selectedEventType],
  );

  const api = useMemo(
    () => ({
      state,
      toggleDensityStrip: () => dispatch({ type: "TOGGLE_DENSITY_STRIP" }),
      setDensityStrip: (visible: boolean) =>
        dispatch({ type: "SET_DENSITY_STRIP", visible }),
      addBookmark: (event: ClinicalEvent) =>
        dispatch({ type: "ADD_BOOKMARK", event }),
      removeBookmark: (eventId: string) =>
        dispatch({ type: "REMOVE_BOOKMARK", eventId }),
      enqueueClip: (event: ClinicalEvent) =>
        dispatch({ type: "ENQUEUE_CLIP", event }),
      dequeueClip: (eventId: string) =>
        dispatch({ type: "DEQUEUE_CLIP", eventId }),
      setEventType: (eventType: string | "all") =>
        dispatch({ type: "SET_EVENT_TYPE", eventType }),
      toggleBaselineComparison: () =>
        dispatch({ type: "TOGGLE_BASELINE_COMPARISON" }),
      jumpCandidates,
    }),
    [state, jumpCandidates],
  );

  return api;
}
